import { DEFAULT_SOURCE, DEFAULT_VIEW_STATE, SOURCES, type SourceKey } from '@/app/config'
import type { MapViewState } from '@/components/map/state/map-view-state'
import type { DisplayMetric } from '@/app/state/display'
import { parseCursorTimeMs } from '@/app/time/cursor-url'

export type IndexRouteSearch = {
  from?: string
  to?: string
  cursor?: string
  speed?: number
  s?: string
  m?: DisplayMetric
  center?: string
  zoom?: number
  bearing?: number
  tilt?: number
  'display.inundation'?: 0 | 1
  'display.precipitation'?: 0 | 1
  'data.src'?: SourceKey
  calendarMode?: string
  calendarMetric?: string
  chartMode?: 'split' | 'combined'
  embed?: 0 | 1
}

export const DEFAULT_FROM = 'now-2d'
export const DEFAULT_TO = 'now'

export const parseSearch = (search: Record<string, unknown>): IndexRouteSearch => ({
  from: typeof search.from === 'string' ? search.from : undefined,
  to: typeof search.to === 'string' ? search.to : undefined,
  cursor: parseCursorTime(search.cursor ?? search.cursorTime),
  speed: parsePlaybackSpeed(search.speed ?? search.playbackSpeed),
  s:
    typeof search.s === 'string'
      ? search.s
      : (typeof search.selectedFeatureKeys === 'string' ? search.selectedFeatureKeys : undefined),
  m: parseDisplayMetric(search.m) ?? parseDisplayMetric(search.displayMetric),
  center: typeof search.center === 'string' ? search.center : undefined,
  zoom: typeof search.zoom === 'number' ? search.zoom : parseFiniteNumber(search.zoom),
  bearing: typeof search.bearing === 'number' ? search.bearing : parseFiniteNumber(search.bearing),
  tilt: typeof search.tilt === 'number' ? search.tilt : parseFiniteNumber(search.tilt),
  'display.inundation': parseEnabledSearchFlag(search['display.inundation']),
  'display.precipitation': parseEnabledSearchFlag(search['display.precipitation']),
  'data.src': parseSourceKey(search['data.src'] ?? search.sourceKey),
  calendarMode: typeof search.calendarMode === 'string' ? search.calendarMode : undefined,
  calendarMetric: typeof search.calendarMetric === 'string' ? search.calendarMetric : undefined,
  chartMode: parseChartMode(search.chartMode),
  embed: parseEnabledSearchFlag(search.embed),
})

export const parseSelectedFeatureKeys = (value?: string): string[] => {
  if (!value) return []
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

export const serializeSelectedFeatureKeys = (keys: string[]): string | undefined => {
  if (!keys.length) return undefined
  return keys.join(',')
}

export const parseFiniteNumber = (value: unknown): number | undefined => {
  if (value == null || value === '') return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

export const parseCursorTime = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const cursor = value.trim()
  const timeMs = parseCursorTimeMs(cursor)
  if (timeMs == null) return undefined
  const absoluteTimeMs = Date.parse(cursor)
  return Number.isFinite(absoluteTimeMs) ? new Date(absoluteTimeMs).toISOString() : cursor
}

export const parseDisplayMetric = (value: unknown): DisplayMetric | undefined => {
  if (value === 'max' || value === 'overTime') return value
  return undefined
}

export const parsePlaybackSpeed = (value: unknown): number | undefined => {
  const parsed = parseFiniteNumber(value)
  return parsed != null && parsed > 0 ? parsed : undefined
}

export const parseEnabledSearchFlag = (value: unknown): 0 | 1 | undefined => {
  if (value === 1 || value === '1') return 1
  if (value === 0 || value === '0') return 0
  return undefined
}

export const parseChartMode = (value: unknown): 'split' | 'combined' | undefined => {
  if (value === 'split' || value === 'combined') return value
  return undefined
}

export const parseSourceKey = (value: unknown): SourceKey | undefined => {
  if (typeof value !== 'string') return undefined
  if (!(value in SOURCES)) return undefined
  return value as SourceKey
}

export const serializeSourceKey = (value: SourceKey): SourceKey | undefined => {
  return value === DEFAULT_SOURCE ? undefined : value
}

export const parseCenter = (value?: string): { latitude: number; longitude: number } | undefined => {
  if (!value) return undefined
  const [latRaw, lngRaw] = value.split(',').map((v) => v.trim())
  const latitude = parseFiniteNumber(latRaw)
  const longitude = parseFiniteNumber(lngRaw)
  if (latitude == null || longitude == null) return undefined
  return { latitude, longitude }
}

export const formatNumber = (value: number, digits: number): string => value.toFixed(digits)

export const parseInitialMapViewState = (search: IndexRouteSearch): MapViewState => {
  const center = parseCenter(search.center)
  const zoom = search.zoom
  const bearing = search.bearing
  const tilt = search.tilt

  return {
    latitude: center?.latitude ?? DEFAULT_VIEW_STATE.latitude,
    longitude: center?.longitude ?? DEFAULT_VIEW_STATE.longitude,
    zoom: zoom ?? DEFAULT_VIEW_STATE.zoom,
    bearing: bearing ?? DEFAULT_VIEW_STATE.bearing,
    pitch: tilt ?? DEFAULT_VIEW_STATE.pitch,
  }
}
